import { metrics, type Histogram } from '@opentelemetry/api';
import type { AspectRatio } from '../aspectRatioConfig';
import {
    VALID_SLIDE_COUNTS,
    VALID_RESOLUTIONS,
    VALID_ASPECT_RATIOS,
    formatResolution,
    formatSlideCount,
} from './label-util';

/**
 * Contains definitions for all metrics to be recorded in Prometheus. Any metric that is timed using
 * the LuminaryActivityProfiler must be defined here.
 *
 * !!WARNING!!: Do not change any existing definitions unless you are ok with potentially invalidating
 * all previous observations for the same metric.
 *
 * @example
 * A metric definition is an object with the following shape:
 *
 * interace labelMappingInterface = {
 *   [key: string]: string[]
 * }
 *
 * declare function getLabelsFct(...args: any[]):labelMappingInterface
 *
 * interface metric =
 * {
 *  name:string // see https://prometheus.io/docs/practices/naming/#metric-names
 *  description:string // a user friendly explanation of what's being observed
 *  type:string  a prometheus metric type compatible with promjs -- one of { counter, gauge, histogram }
 *  labelValueMapping:labelMappingInterface // a key value pairing of label names to valid label values. The object
 *      // key represents a unique label name and the value is a finite set of unique values that label can have.
 *      // Observations for that metric will be validated to ensure all labels recorded here are provided a value
 *      // from its unique value set.
 *  histogramBuckets:number[]? // if the metric type is a histogram, this is a list of integers
 *  getLabels?: getLabelsFct // a helper method to transform arguments into the expected label set. Recommended
 *      to implement for consistency
 * }
 */

// If a metrics provider is not registered in Node, a noop meter provider is returned and nothing will be recorded
const meterProvider = metrics.getMeterProvider();

const DEFAULT_TIMING_BUCKETS_MS = [10, 20, 50, 100, 200, 500, 1000, 5000, 100000];

export const METRIC_TYPE_HISTOGRAM = 'histogram';

type MetricDefinition = {
    name: string;
    description: string;
    type: string;
    histogramBuckets: number[];
    labelValueMapping: Record<string, any>;
    getLabels?: (...args) => Record<string, any>;
    node?: Histogram;
};

const wrapMetric = (metricDefn: MetricDefinition) => {
    if (metricDefn.type === METRIC_TYPE_HISTOGRAM) {
        metricDefn.node = meterProvider
            .getMeter('video-data-server')
            .createHistogram(metricDefn.name, {
                description: metricDefn.description,
                advice: {
                    explicitBucketBoundaries: metricDefn.histogramBuckets,
                },
            });
    }
    return metricDefn;
};

export const METRIC_LOAD_NAME = 'luminary_load_performance_ms';
export const METRIC_LOAD = {
    name: METRIC_LOAD_NAME,
    description: 'Execution time to synchronously load video-data as luminary state',
    type: METRIC_TYPE_HISTOGRAM,
    histogramBuckets: DEFAULT_TIMING_BUCKETS_MS,
    labelValueMapping: {
        slideCount: Object.values(VALID_SLIDE_COUNTS),
    },
    getLabels: function (luminarySlideCount: number) {
        return {
            slideCount: formatSlideCount(luminarySlideCount),
        };
    },
};

export const METRIC_RENDER_WORKSPACE_NAME = 'luminary_render_workspace_performance_ms';
export const METRIC_RENDER_WORKSPACE = {
    name: METRIC_RENDER_WORKSPACE_NAME,
    description:
        'Execution time to synchronously render slides and their children to a specific target',
    type: METRIC_TYPE_HISTOGRAM,
    histogramBuckets: DEFAULT_TIMING_BUCKETS_MS,
    labelValueMapping: {
        target: ['active_slides', 'single_slide'],
        resolution: Object.values(VALID_RESOLUTIONS),
        aspectRatio: Object.values(VALID_ASPECT_RATIOS),
    },
    getLabels(renderAllScenes: boolean, resolution: number, aspectRatio: AspectRatio) {
        const target = renderAllScenes ? 'active_slides' : 'single_slide';
        return {
            target,
            resolution: formatResolution(resolution),
            aspectRatio: aspectRatio,
        };
    },
};

export const METRIC_SAVE_NAME = 'luminary_save_performance_ms';
export const METRIC_SAVE = {
    name: METRIC_SAVE_NAME,
    description:
        'Execution time to synchronously transform luminary state to video data object representation',
    type: METRIC_TYPE_HISTOGRAM,
    histogramBuckets: DEFAULT_TIMING_BUCKETS_MS,
    labelValueMapping: {
        slideCount: Object.values(VALID_SLIDE_COUNTS),
    },
    getLabels: function (slideCount: number) {
        return {
            slideCount: formatSlideCount(slideCount),
        };
    },
};

export const METRICS_TO_RECORD = [METRIC_LOAD, METRIC_SAVE, METRIC_RENDER_WORKSPACE];

export const METRIC_NAME_MAPPING = {
    [METRIC_LOAD_NAME]: wrapMetric(METRIC_LOAD),
    [METRIC_SAVE_NAME]: wrapMetric(METRIC_SAVE),
    [METRIC_RENDER_WORKSPACE_NAME]: wrapMetric(METRIC_RENDER_WORKSPACE),
};
